import React, { useEffect, useState } from 'react'
import AdminNavbar from './AdminNavbar'
import BulkFileUploader from './MediaFiles'
import { callAPI } from "../feature/APISlice";
import { useDispatch, useSelector } from 'react-redux';

const BASE_URL = `${process.env.REACT_APP_PRODUCTION_API_URL}backend/api/admin/media`;

export default function ImagesAndVideos() {
    const dispatch = useDispatch();
    const { items, loading, error } = useSelector((s) => s.api);
    const [copiedId, setCopiedId] = useState(null);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [editingMedia, setEditingMedia] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        dispatch(callAPI({ method: "get", url: BASE_URL }));
    }, [dispatch]);

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleMediaClick = (item) => {
        setSelectedMedia(item);
        setShowPreview(true);
    };

    const handleClosePreview = () => {
        setShowPreview(false);
        setSelectedMedia(null);
        setEditingMedia(null);
    };

    const handlePrevious = () => {
        if (!items || items.length === 0) return;
        const currentIndex = items.findIndex(item => item.id === selectedMedia.id);
        const previousIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        setSelectedMedia(items[previousIndex]);
    };

    const handleNext = () => {
        if (!items || items.length === 0) return;
        const currentIndex = items.findIndex(item => item.id === selectedMedia.id);
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        setSelectedMedia(items[nextIndex]);
    };

    const handleEditClick = (item) => {
        setEditingMedia({...item});
    };

    const handleSaveEdit = async () => {
        if (!editingMedia) return;
        
        try {
            setIsUploading(true);
            await dispatch(callAPI({
                method: "put",
                url: `${BASE_URL}/${editingMedia.id}`,
                data: editingMedia
            })).unwrap();
            
            dispatch(callAPI({ method: "get", url: BASE_URL }));
            setEditingMedia(null);
            setIsUploading(false);
        } catch (error) {
            console.error('Error updating media:', error);
            setIsUploading(false);
        }
    };

    const handleDeleteMedia = async (id) => {
        if (window.confirm('Are you sure you want to delete this media file?')) {
            try {
                await dispatch(callAPI({
                    method: "delete",
                    url: `${BASE_URL}/${id}`
                })).unwrap();
                
                dispatch(callAPI({ method: "get", url: BASE_URL }));
                if (selectedMedia?.id === id) {
                    handleClosePreview();
                }
            } catch (error) {
                console.error('Error deleting media:', error);
            }
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('RIVID', editingMedia.RIVID);
            formData.append('is_active', editingMedia.is_active);

            await dispatch(callAPI({
                method: "put",
                url: `${BASE_URL}/${editingMedia.id}/file`,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })).unwrap();

            dispatch(callAPI({ method: "get", url: BASE_URL }));
            setIsUploading(false);
            setEditingMedia(null);
        } catch (error) {
            console.error('Error updating file:', error);
            setIsUploading(false);
        }
    };

    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: showPreview ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
    };

    const modalContentStyle = {
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative'
    };

    const modalHeaderStyle = {
        padding: '20px',
        borderBottom: '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    };

    const modalBodyStyle = {
        display: 'flex',
        height: '70vh'
    };

    const previewContainerStyle = {
        flex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000',
        position: 'relative'
    };

    const infoPanelStyle = {
        flex: 1,
        padding: '20px',
        overflowY: 'auto',
        borderLeft: '1px solid #eee'
    };

    const largeImageStyle = {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
    };

    const largeVideoStyle = {
        width: '100%',
        height: '100%'
    };

    const navButtonStyle = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        border: 'none',
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        fontSize: '20px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const prevButtonStyle = {
        ...navButtonStyle,
        left: '20px'
    };

    const nextButtonStyle = {
        ...navButtonStyle,
        right: '20px'
    };

    const closeButtonStyle = {
        position: 'absolute',
        top: '15px',
        right: '15px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        border: 'none',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        fontSize: '20px',
        cursor: 'pointer',
        zIndex: 1001
    };

    const containerStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
    };

    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
        marginTop: '30px'
    };

    const cardStyle = {
        border: '1px solid #ddd',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s',
        backgroundColor: 'white',
        cursor: 'pointer'
    };

    const cardHoverStyle = {
        transform: 'translateY(-5px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
    };

    const mediaContainerStyle = {
        position: 'relative',
        paddingTop: '56.25%',
        backgroundColor: '#f5f5f5'
    };

    const imageStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    };

    const rivIdBadgeStyle = {
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold'
    };

    const contentStyle = {
        padding: '15px'
    };

    const loadingStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '18px',
        color: '#666'
    };

    const errorStyle = {
        padding: '15px',
        backgroundColor: '#ffebee',
        color: '#c62828',
        borderRadius: '4px',
        margin: '20px 0'
    };

    if (loading) {
        return (
            <div>
                <AdminNavbar />
                <div style={loadingStyle}>
                    <div>Loading media files...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <AdminNavbar />
                <div style={containerStyle}>
                    <div style={errorStyle}>
                        Error loading media files: {error}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <AdminNavbar />
            <div style={containerStyle}>
                <BulkFileUploader />
                
                <h2 style={{ marginTop: '30px', marginBottom: '20px' }}>Media Library</h2>
                
                {items && items.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                        <h3>No media files found</h3>
                        <p>Upload some files using the uploader above.</p>
                    </div>
                ) : (
                    <div style={gridStyle}>
                        {items?.map((item) => (
                            <div 
                                key={item.id} 
                                style={cardStyle}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                onClick={() => handleMediaClick(item)}
                            >
                                <div style={mediaContainerStyle}>
                                    {item.type === 'image' ? (
                                        <img
                                            src={item.url}
                                            alt={item.name}
                                            style={imageStyle}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://via.placeholder.com/300x169?text=Image+Not+Found';
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            ...imageStyle,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#333',
                                            color: 'white'
                                        }}>
                                            <svg style={{ width: '48px', height: '48px', marginRight: '10px' }} fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z"/>
                                            </svg>
                                            <span>Video File</span>
                                        </div>
                                    )}
                                    
                                    <div style={rivIdBadgeStyle}>
                                        RIVId: {item.RIVID}
                                    </div>
                                </div>
                                
                                <div style={contentStyle}>
                                    <div style={{display: 'inline-flex', alignItems: 'center', padding: '4px 8px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '12px', marginBottom: '10px'}}>
                                        {item.type.toUpperCase()}
                                    </div>
                                    
                                    <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>
                                        {item.name.length > 30 ? `${item.name.substring(0, 30)}...` : item.name}
                                    </h3>
                                    
                                    <div style={{display: 'inline-block', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', backgroundColor: item.is_active ? '#e8f5e9' : '#ffebee', color: item.is_active ? '#2e7d32' : '#c62828'}}>
                                        {item.is_active ? '✓ Active' : '✗ Inactive'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Preview Modal */}
                <div style={modalOverlayStyle} onClick={(e) => e.target === e.currentTarget && handleClosePreview()}>
                    {selectedMedia && (
                        <div style={modalContentStyle}>
                            <button style={closeButtonStyle} onClick={handleClosePreview}>
                                ×
                            </button>
                            
                            <div style={modalHeaderStyle}>
                                <h2 style={{margin: 0}}>
                                    {selectedMedia.name} (RIVId: {selectedMedia.RIVID})
                                </h2>
                               
                            </div>
                            
                            <div style={modalBodyStyle}>
                                <div style={previewContainerStyle}>
                                    {selectedMedia.type === 'image' ? (
                                        <img
                                            src={selectedMedia.url}
                                            alt={selectedMedia.name}
                                            style={largeImageStyle}
                                        />
                                    ) : (
                                        <video
                                            controls
                                            style={largeVideoStyle}
                                            src={selectedMedia.url}
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    )}
                                    
                                    {items.length > 1 && (
                                        <>
                                            <button style={prevButtonStyle} onClick={handlePrevious}>
                                                ‹
                                            </button>
                                            <button style={nextButtonStyle} onClick={handleNext}>
                                                ›
                                            </button>
                                        </>
                                    )}
                                </div>
                                
                                <div style={infoPanelStyle}>
                                    {editingMedia ? (
                                        <div>
                                            <h3>Edit Media</h3>
                                            <div style={{marginBottom: '15px'}}>
                                                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>RIVId:</label>
                                                <input
                                                    type="number"
                                                    value={editingMedia.RIVID}
                                                    onChange={(e) => setEditingMedia({...editingMedia, RIVID: e.target.value})}
                                                    style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                                                />
                                            </div>
                                            
                                            <div style={{marginBottom: '15px'}}>
                                                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Status:</label>
                                                <select
                                                    value={editingMedia.is_active}
                                                    onChange={(e) => setEditingMedia({...editingMedia, is_active: e.target.value === 'true'})}
                                                    style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
                                                >
                                                    <option value="true">Active</option>
                                                    <option value="false">Inactive</option>
                                                </select>
                                            </div>
                                            
                                            <div style={{marginBottom: '15px'}}>
                                                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Replace File:</label>
                                                <input
                                                    type="file"
                                                    onChange={handleFileChange}
                                                    accept={selectedMedia.type === 'image' ? 'image/*' : 'video/*'}
                                                    style={{width: '100%', padding: '8px'}}
                                                />
                                            </div>
                                            
                                            <div style={{display: 'flex', gap: '10px'}}>
                                                <button 
                                                    style={{flex: 1, padding: '10px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                                                    onClick={handleSaveEdit}
                                                    disabled={isUploading}
                                                >
                                                    {isUploading ? 'Saving...' : 'Save Changes'}
                                                </button>
                                                <button 
                                                    style={{flex: 1, padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer'}}
                                                    onClick={() => setEditingMedia(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <h3>Media Details</h3>
                                            <div style={{marginBottom: '15px'}}>
                                                <strong>Type:</strong> {selectedMedia.type.toUpperCase()}
                                            </div>
                                            <div style={{marginBottom: '15px'}}>
                                                <strong>Filename:</strong> {selectedMedia.name}
                                            </div>
                                            <div style={{marginBottom: '15px'}}>
                                                <strong>URL:</strong>
                                                <div style={{wordBreak: 'break-all', fontSize: '12px', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', marginTop: '5px'}}>
                                                    {selectedMedia.url}
                                                </div>
                                                <button 
                                                    style={{padding: '6px 12px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginTop: '5px'}}
                                                    onClick={() => copyToClipboard(selectedMedia.url, 'preview-url')}
                                                >
                                                    {copiedId === 'preview-url' ? '✓ Copied!' : 'Copy URL'}
                                                </button>
                                            </div>
                                            <div style={{marginBottom: '15px'}}>
                                                <strong>Created:</strong> {formatDate(selectedMedia.createdTime)} by {selectedMedia.createdByName}
                                            </div>
                                            <div style={{marginBottom: '15px'}}>
                                                <strong>Last Modified:</strong> {formatDate(selectedMedia.updatedTime)} by {selectedMedia.lastModifiedByName}
                                            </div>
                                            <div style={{marginBottom: '15px'}}>
                                                <strong>Status:</strong> 
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '4px 8px',
                                                    marginLeft: '10px',
                                                    borderRadius: '4px',
                                                    backgroundColor: selectedMedia.is_active ? '#e8f5e9' : '#ffebee',
                                                    color: selectedMedia.is_active ? '#2e7d32' : '#c62828'
                                                }}>
                                                    {selectedMedia.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}